import { useState, useCallback, useMemo } from 'react';

const DEFAULT_FILTERS = {
  position: 'ALL',
  team: 'ALL',
  search: '',
  sortBy: 'baseline_price',
  sortOrder: 'desc',
  // 8m.9 G.1 fold-in: all 5 numeric filter fields explicit. Pre-fix only
  // declared minAB + minIP; the other 3 were lazily added on first user
  // edit. Now state shape is fully declared upfront — matches what
  // DraftFilters.js reads via `filters.minG ?? 0` etc.
  minG: 0,             // For hitters (games played)
  minAB: 0,            // For hitters (at-bats)
  minGPitching: 0,     // For pitchers (games)
  minGS: 0,            // For pitchers (games started)
  minIP: 0             // For pitchers (innings pitched)
};

export const useDraftFilters = () => {
  const [filters, setFiltersState] = useState(DEFAULT_FILTERS);

  // Individual setters for granular control
  const setPositionFilter = useCallback((position) => {
    setFiltersState(prev => ({ ...prev, position }));
  }, []);

  const setTeamFilter = useCallback((team) => {
    setFiltersState(prev => ({ ...prev, team }));
  }, []);

  const setSearchTerm = useCallback((search) => {
    setFiltersState(prev => ({ ...prev, search }));
  }, []);

  const setSortBy = useCallback((sortBy) => {
    setFiltersState(prev => ({ ...prev, sortBy }));
  }, []);

  const setSortOrder = useCallback((sortOrder) => {
    setFiltersState(prev => ({ ...prev, sortOrder }));
  }, []);

  // Batch setter for multiple filters at once
  const setFilters = useCallback((newFilters) => {
    setFiltersState(prev => ({ ...prev, ...newFilters }));
  }, []);

  // Reset all filters to defaults
  const resetFilters = useCallback(() => {
    setFiltersState(DEFAULT_FILTERS);
  }, []);

  // Computed: check if any filters are active.
  // 8m.9 G.2 fold-in: extended to include 5 numeric filter fields. Pre-fix
  // user could set minAB=200 (or any numeric filter) and hasActiveFilters
  // would still return false — "Reset Filters" UI affordance wouldn't
  // appear despite an actually-active filter state.
  const hasActiveFilters = useMemo(() => {
    return (
      filters.position !== DEFAULT_FILTERS.position ||
      filters.team !== DEFAULT_FILTERS.team ||
      filters.search !== DEFAULT_FILTERS.search ||
      filters.sortBy !== DEFAULT_FILTERS.sortBy ||
      filters.sortOrder !== DEFAULT_FILTERS.sortOrder ||
      filters.minG !== DEFAULT_FILTERS.minG ||
      filters.minAB !== DEFAULT_FILTERS.minAB ||
      filters.minGPitching !== DEFAULT_FILTERS.minGPitching ||
      filters.minGS !== DEFAULT_FILTERS.minGS ||
      filters.minIP !== DEFAULT_FILTERS.minIP
    );
  }, [filters]);

  return {
    filters,
    setPositionFilter,
    setTeamFilter,
    setSearchTerm,
    setSortBy,
    setSortOrder,
    setFilters,
    resetFilters,
    hasActiveFilters
  };
};
